import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { PageHeader } from '../components/Section';
import { formatCredits } from '../hooks/useCredits';
import { format, useTranslation } from '../i18n';

type AdminUser = {
  id: number;
  email: string | null;
  name: string | null;
  role: string;
  enabled: boolean;
  credits: number;
  created_at: string;
  last_login: string | null;
  jobs_count: number;
  credits_spent: number;
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function CreditDialog({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useTranslation();
  const [mode, setMode] = useState<'add' | 'set'>('add');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const n = Number(amount);
    if (!Number.isInteger(n)) {
      setError(t.adminUsers.dialog.amountError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/admin/users/${user.id}/credits`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, amount: n }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || format(t.adminUsers.requestFailed, { status: res.status }));
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t.adminUsers.dialog.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {format(t.adminUsers.dialog.balance, {
              who: user.email || user.name || format(t.adminUsers.userFallback, { id: user.id }),
              credits: formatCredits(user.credits),
            })}
          </Typography>
          <RadioGroup
            row
            value={mode}
            onChange={(e) => setMode(e.target.value as 'add' | 'set')}
          >
            <FormControlLabel value="add" control={<Radio size="small" />} label={t.adminUsers.dialog.add} />
            <FormControlLabel value="set" control={<Radio size="small" />} label={t.adminUsers.dialog.setTo} />
          </RadioGroup>
          <TextField
            label={t.adminUsers.dialog.amount}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            size="small"
            autoFocus
            helperText={mode === 'add' ? t.adminUsers.dialog.helperAdd : t.adminUsers.dialog.helperSet}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t.common.cancel}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || amount === ''}>
          {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t.common.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Users() {
  const t = useTranslation();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const load = async () => {
    try {
      const res = await fetch('/api/v1/admin/users', { credentials: 'include' });
      if (!res.ok) throw new Error(format(t.adminUsers.loadError, { status: res.status }));
      const data = (await res.json()) as { items: AdminUser[] };
      setUsers(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <Box>
      <PageHeader title={t.adminUsers.title} description={t.adminUsers.description} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {users == null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t.adminUsers.table.user}</TableCell>
                  <TableCell>{t.adminUsers.table.role}</TableCell>
                  <TableCell align="right">{t.adminUsers.table.credits}</TableCell>
                  <TableCell align="right">{t.adminUsers.table.jobs}</TableCell>
                  <TableCell align="right">{t.adminUsers.table.spent}</TableCell>
                  <TableCell>{t.adminUsers.table.lastLogin}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.id}
                    hover
                    onClick={() => setSelected(u)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {u.email || u.name || format(t.adminUsers.userFallback, { id: u.id })}
                      </Typography>
                      {u.email && u.name && (
                        <Typography variant="caption" color="text.secondary">
                          {u.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.role !== 'user' && (
                        <Chip
                          size="small"
                          label={u.role}
                          color={u.role === 'global_admin' ? 'primary' : 'default'}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatCredits(u.credits)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {u.jobs_count}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatCredits(u.credits_spent)}
                    </TableCell>
                    <TableCell>{formatDate(u.last_login)}</TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {t.adminUsers.empty}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {selected && (
        <CreditDialog
          user={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            void load();
          }}
        />
      )}
    </Box>
  );
}
